/*
 * Enrolling this device for Web Push (stage 4a).
 *
 * The browser half of a three-part arrangement: this registers the service
 * worker and mints a subscription, `internal/api` stores it, and `internal/push`
 * sends to it at the end of the poll cycle that wrote the alert (decision 485's
 * window).
 *
 * # Everything here is behind a gesture, and that is not only a rule
 *
 * "Never prompt for permission on load" is Kevin's ruling, and the platforms
 * enforce a stricter version of it than the rule states: Safari requires
 * `pushManager.subscribe` to be called from inside a user-gesture handler, and
 * Chrome requires the same of `Notification.requestPermission` on some releases.
 * So `enablePush` must be awaited from a click handler, and it takes the
 * application server key as an ARGUMENT rather than fetching it — a network
 * round trip between the tap and the prompt is enough to lose the gesture.
 *
 * # The permission is the device's mute, and there is no other
 *
 * A subscription is per DEVICE where preferences are per (user, list) — decision
 * 394's key with decision 486's ladder on top. Those are different granularities
 * on purpose: WHICH kinds reach a pocket is the preference matrix's question,
 * resolved per list and OR'd across the lists holding the fencer (decision 419),
 * and WHETHER this phone is reachable at all is this file's. A per-device mute is
 * deliberately not invented; unenrolling the device is it.
 *
 * # iOS
 *
 * iOS delivers Web Push only to an INSTALLED progressive web app. There is no
 * install tutorial here (Kevin's ruling): a browser that cannot subscribe
 * reports itself unsupported and the control renders one line saying so. The
 * `manifest.webmanifest` is what makes installing possible; explaining how to do
 * it is not this screen's job.
 */

/** Whether this browser can be enrolled at all. */
export function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Whether the user has permanently refused. */
export function pushBlocked(): boolean {
  return pushSupported() && Notification.permission === "denied";
}

/**
 * Whether the app is running INSTALLED — from the home screen or as an
 * installed app — rather than in a browser tab.
 *
 * It decides which sentence a blocked permission gets, because the setting
 * lives in a different place: a tab's block is in the browser's settings for
 * the site, an installed app's is in the device's notification settings. It
 * never selects per-browser directions; there are none, because directions
 * for one browser misdirect every other.
 *
 * `navigator.standalone` is iOS Safari's own flag and predates the media query
 * there. Both are guarded, because jsdom has no `matchMedia` and neither is
 * worth throwing over.
 */
export function installedApp(): boolean {
  try {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      if (window.matchMedia("(display-mode: standalone)").matches) return true;
    }
  } catch {
    // Fall through to the iOS flag.
  }
  return (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Register the worker, or reuse the registration if it is already there, and in
 * either case return it only once its worker is ACTIVE — see `activated` below
 * for why the second half is not a nicety.
 *
 * SCOPE IS THE WHOLE POINT AND IT COMES FROM THE PATH. A worker's default scope
 * is the directory it was served from, so `/sw.js` controls the whole origin and
 * `/assets/sw-abc123.js` would control `/assets/*` — registering without error,
 * receiving no push for the app's own pages, and looking entirely installed.
 * vite.sw.config.ts emits it at the root and internal/api/spa_test.go pins that
 * it is served from there.
 *
 * ON A NON-CANONICAL HOST THIS FAILS, LOUDLY, AND THAT IS THE SAFE DIRECTION.
 * `TC_BASE_URL` is the only origin authority (decisions 309, 383) and cmd/api
 * answers a non-canonical Host with a 301 on GET — and the specification refuses
 * a service worker script that REDIRECTS, so a registration attempted on `www`
 * throws rather than installing at the wrong scope. In practice it never gets
 * that far: the document request is redirected first, so the app is running on
 * the canonical origin before any of this executes. A push subscription is bound
 * to the origin that created it, so the passkey-on-`www` failure has no analogue
 * here — there is no way to end up holding a live subscription on the wrong one.
 */
async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) {
    await checkForUpdate(existing);
    return activated(existing);
  }
  return activated(await navigator.serviceWorker.register("/sw.js", { scope: "/" }));
}

/**
 * Ask the browser to refetch `/sw.js` for an already-installed registration.
 *
 * `update()` rejects on a failed fetch — swallowed here, because a network
 * failure checking for a newer worker must leave the installed one in place
 * and must not affect the caller. Safe to do eagerly: `sw.ts` registers only
 * `install`, `activate`, `push` and `notificationclick` — there is no `fetch`
 * handler — so a worker swapping mid-session controls nothing the page
 * depends on, and it already calls `skipWaiting()`/`clients.claim()` so a
 * fetched worker activates immediately. `/sw.js` is served `no-cache`
 * (`internal/api/spa.go`), so the request is cheap and correctly revalidated.
 */
async function checkForUpdate(reg: ServiceWorkerRegistration): Promise<void> {
  try {
    await reg.update();
  } catch {
    // The installed worker still works; the next check tries again.
  }
}

/**
 * How long to wait for a freshly registered worker to become active.
 *
 * Generous, because it is not a latency budget: on every path that matters the
 * worker is already active and this returns without waiting at all. It exists
 * only so a worker that never activates fails as an error the caller can report
 * rather than as a button that stays disabled for the rest of the session.
 */
const activationTimeoutMs = 10_000;

/**
 * Resolve once a registration has an ACTIVE worker.
 *
 * THE FIRST TAP ON A FRESH INSTALL USED TO FAIL, and this is why.
 * `navigator.serviceWorker.register` resolves when the registration exists and
 * its worker is INSTALLING — not when it is active. `pushManager.subscribe`
 * needs an active worker and WebKit rejects the call without one, so the first
 * ever tap on an installed PWA registered the worker, subscribed against it a
 * moment too early, threw, and was flattened by enablePush's catch into "Could
 * not save that. Try again." Tapping again worked, because by then the worker
 * had activated and `getRegistration` returned it. Observed on iOS on
 * 2026-09-12: the server logged exactly ONE request for the two taps, which is
 * what rules out the network and puts the fault before `save`.
 *
 * `navigator.serviceWorker.ready` is the wait, and it is the right one because
 * it resolves for the registration whose scope covers this page — which is `/`,
 * the scope this file insists on above. It also never rejects and never settles
 * if activation never happens, hence the timeout: an unbounded await inside the
 * click handler would leave the control spinning with nothing to say.
 *
 * THIS DOES NOT COST THE GESTURE, and the ordering is what makes that true.
 * `Notification.requestPermission` is the call the platforms require a gesture
 * for and it has already returned by the time anything here runs; the wait is
 * after the prompt rather than before it. And `prepareRegistration` below starts
 * the worker when the settings page mounts, so by the time a reader has found
 * the button and pressed it, `reg.active` is set and this returns immediately.
 */
async function activated(reg: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  if (reg.active) return reg;

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error("push: the service worker did not activate")),
          activationTimeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Register the worker ahead of the tap, from the page that offers the button.
 *
 * NOT A PERMISSION PROMPT, so it does not touch "never prompt on load" (Kevin's
 * ruling): registering a worker asks the reader nothing and shows them nothing.
 * What it buys is that the activation `activated` waits for has already happened
 * before the button is pressed, which turns the fix above from a wait into a
 * no-op on every visit — including the first, because a reader has to read the
 * offer before deciding to accept it.
 *
 * Silent and best-effort, like `syncSubscription`: a failure here costs the
 * first tap a wait and nothing else, and the click handler reports for itself.
 */
export async function prepareRegistration(): Promise<void> {
  if (!pushSupported()) return;
  try {
    await registration();
  } catch {
    // The tap will try again and say so if it fails there.
  }
}

/**
 * Check an already-enrolled device's worker for an update, once per app
 * start.
 *
 * A reader who never opens Settings never runs `prepareRegistration`, so an
 * installed PWA that stays open for days can go on running a stale worker
 * indefinitely — a push event does not trigger an update check on its own.
 * This is that check, run unconditionally at boot rather than gated behind a
 * screen.
 *
 * GUARDED ON AN EXISTING REGISTRATION, deliberately not calling
 * `registration()`: registering a worker for someone who has never enrolled
 * in push would be a behaviour change, where updating a registration that
 * does not exist is simply a no-op.
 */
export async function refreshServiceWorker(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) await checkForUpdate(existing);
  } catch {
    // Nothing here is worth failing app start over.
  }
}

/** This device's current subscription, or null. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    // A browser that throws while being asked is a browser that is not enrolled.
    // Nothing here is worth failing a settings page over.
    return null;
  }
}

/**
 * The application server key as `pushManager.subscribe` wants it.
 *
 * It takes raw BYTES where the server, the browser and every example on the web
 * speak base64url, so the conversion has to happen somewhere; here, once, rather
 * than at the call site. `atob` needs the standard alphabet and the padding that
 * base64url drops.
 */
function applicationServerKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), "=");
  const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  // The buffer is allocated explicitly so the type is Uint8Array<ArrayBuffer>
  // rather than Uint8Array<ArrayBufferLike>: `BufferSource` excludes
  // SharedArrayBuffer, and the plain constructor's type does not.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * What happened when the user asked to be notified.
 *
 * `dismissed` IS NOT `denied`. The prompt resolving `"default"` means the reader
 * closed it without answering, and the browser will ask again on the next tap,
 * so the button stays — by the same reasoning as a failed attempt keeping it
 * (decision 498). Only `"denied"` is a refusal the page cannot re-arm.
 */
export type EnableResult = "enabled" | "denied" | "dismissed" | "unsupported" | "failed";

/**
 * Ask for permission and enrol this device. MUST be called from a gesture.
 *
 * @param publicKey the VAPID key from /api/me
 * @param save what to do with the subscription — `api.subscribePush`
 */
export async function enablePush(
  publicKey: string,
  save: (sub: PushSubscriptionJSON) => Promise<void>,
): Promise<EnableResult> {
  if (!pushSupported() || !publicKey) return "unsupported";

  try {
    const permission = await Notification.requestPermission();
    if (permission === "default") return "dismissed";
    if (permission !== "granted") return "denied";

    const reg = await registration();
    // `userVisibleOnly` is required by every implementation and is also the
    // promise this product intends to keep: every push shows a notification, and
    // the worker has no branch that can end without one.
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(publicKey),
    });

    await save(sub.toJSON());
    return "enabled";
  } catch {
    // Registration refused, a key that will not parse, the network. All of them
    // leave the device unenrolled, which is one state with one remedy: try again.
    return "failed";
  }
}

/**
 * Unenrol this device, both sides.
 *
 * THE SERVER SIDE GOES FIRST AND IS AWAITED, because it is the half that needs a
 * session — this is called by sign-out as well as by the Settings control, and
 * after the cookie is cleared the request would be a 401. Then the browser's own
 * subscription is dropped, so `getSubscription()` reports the truth on the next
 * visit.
 *
 * BOTH HALVES ARE BEST-EFFORT and neither can fail the caller. A sign-out that
 * refused to complete because a push endpoint was unreachable would be a
 * stopgap delivery channel holding the session hostage.
 */
export async function disablePush(remove: (endpoint: string) => Promise<void>): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;

  try {
    await remove(sub.endpoint);
  } catch {
    // The row may outlive this device's copy. It is pruned on the first 404 or
    // 410 from the push service, which is what unsubscribing below causes.
  }
  try {
    await sub.unsubscribe();
  } catch {
    // Nothing further to do: the endpoint is already gone server-side.
  }
}

/**
 * Re-send a subscription this browser already holds.
 *
 * WHY THIS EXISTS RATHER THAN A READ ENDPOINT. The two sides can disagree — a
 * transient 410 prunes a live row, an account is deleted and re-made, a database
 * is restored — and the browser is the side that knows the truth about itself.
 * So instead of asking the server what it holds (which would mean an endpoint
 * that returns credentials), the client simply re-asserts what it has whenever
 * the notifications page loads. The upsert makes that free.
 *
 * Silent: there is nothing for a reader to do about it either way.
 */
export async function syncSubscription(
  save: (sub: PushSubscriptionJSON) => Promise<void>,
): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await save(sub.toJSON());
  } catch {
    // Next page load tries again.
  }
}
